import { PopupButton } from "@typeform/embed-react";
import { transparentize } from "polished";
import Styled from "styled-components";

import magicStarIconPurple from "cms/images/magic-star-icon-purple.svg";
import magicStarIconWhite from "cms/images/magic-star-icon-white.svg";
import menuItemToggleCollapseDarkMode from "cms/images/nav-collapse-dark-mode.svg";
import menuItemToggleCollapseLightMode from "cms/images/nav-collapse-light-mode.svg";
import menuItemToggleExpandDarkMode from "cms/images/nav-expand-dark-mode.svg";
import menuItemToggleExpandLightMode from "cms/images/nav-expand-light-mode.svg";
import navToggleCloseDarkMode from "cms/images/nav-toggle-close-dark-mode.svg";
import navToggleCloseLightMode from "cms/images/nav-toggle-close-light-mode.svg";
import navToggleOpenDarkMode from "cms/images/nav-toggle-open-dark-mode.svg";
import navToggleOpenLightMode from "cms/images/nav-toggle-open-light-mode.svg";

import {
  getMagicTextColor,
  getTextColor,
  getTextStyle,
} from "cms/theme/helpers";

const NAV_TOGGLE_ASSETS = {
  light: {
    open: navToggleOpenLightMode,
    close: navToggleCloseLightMode,
  },
  dark: {
    open: navToggleOpenDarkMode,
    close: navToggleCloseDarkMode,
  },
};

const MENU_ITEM_TOGGLE_ASSETS = {
  light: {
    expand: menuItemToggleExpandLightMode,
    collapse: menuItemToggleCollapseLightMode,
  },
  dark: {
    expand: menuItemToggleExpandDarkMode,
    collapse: menuItemToggleCollapseDarkMode,
  },
};

export interface ContainerProps {
  $isScrolling?: boolean;
}

export const Container = Styled.div<ContainerProps>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  background: transparent;
  border-bottom: 1px solid transparent;
  transition: border-color 200ms linear, background-color 200ms linear;

  ${(props) =>
    props.$isScrolling &&
    `
    background: ${
      props.theme.mode === "light"
        ? props.theme.colors.neutrals.white500
        : props.theme.colors.background
    };
        
    border-color: ${
      props.theme.mode === "light"
        ? props.theme.colors.neutrals.gray200
        : props.theme.colors.neutrals.gray800
    };
  `}
`;

export interface BannerProps {
  $variant?: string;
}

export const Banner = Styled.div<BannerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 4.875rem;
  width: 100%;
  padding: 0 1rem;
  text-align: center;
  gap: 0.25rem;

  * {
    ${(props) => getTextStyle(props.theme, "L3", "medium")}
  }

  ${(props) => props.theme.breakpoints.up("medium")} {
    height: 3.375rem;
  }

  ${(props) =>
    props.$variant === "magic" &&
    `
    background: ${props.theme.colors.neutrals.black500};
    background: radial-gradient(29.05% 42.59% at 50% -5.56%, #6419FF 0%, #000 100%);
    color: ${props.theme.colors.neutrals.white500};
    
    a:hover {
      color: ${props.theme.colors.neutrals.gray200};
    }
  `}
`;

export const BannerMagicIcon = Styled.img.attrs(() => ({
  src: magicStarIconWhite,
  alt: "Magic icon",
}))`
  display: block;
  width: 1.5rem;
  height: 1.5rem;
`;

export interface ContentProps {
  $isOpen?: boolean;
}

export const Content = Styled.div<ContentProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2.5rem;
  max-width: 100%;
  width: ${(props) => props.theme.width};
  height: 3.75rem;
  margin: 0 auto;

  ${(props) =>
    props.$isOpen &&
    `
    &:after {
      content: "";
      display: block;
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      height: 2rem;
      background: linear-gradient(
        0deg,
        ${
          props.theme.mode === "light"
            ? transparentize(1, props.theme.colors.neutrals.white500)
            : transparentize(1, props.theme.colors.neutrals.black500)
        } 0%,
        ${
          props.theme.mode === "light"
            ? transparentize(0.33, props.theme.colors.neutrals.white500)
            : transparentize(0.33, props.theme.colors.neutrals.black500)
        } 100%
          );
      z-index: 1;
    }
  `}

  ${(props) => props.theme.breakpoints.up("medium")} {
    height: 5.375rem;
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    position: relative;
    max-width: calc(100% - 2rem);

    &:after {
      display: none;
    }
  }
  
`;

export const Link = Styled.a`
  display: block;
  text-decoration: none;
  height: 100%;
`;

export const LogoContainer = Styled.div`
  height: 1.25rem;
  margin-left: 1.25rem;

  ${(props) => props.theme.breakpoints.up("medium")} {
    margin-left: 1.5rem;
    height: 2rem;
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    margin: 0;
  }
`;

export interface MainProps {
  $isOpen?: boolean;
  $withBanner?: boolean;
}

export const Main = Styled.div<MainProps>`
  position: absolute;
  display: flex;
  flex-direction: column;
  top: 100%;
  left: 0;
  width: 100%;
  opacity: 0;
  visibility: hidden;
  overflow: auto;
  padding: 0rem 1.25rem 1.25rem 1.25rem;
  transition: opacity 200ms linear, visibility 0ms 200ms linear;
  background: ${(props) =>
    props.theme.mode === "light"
      ? props.theme.colors.neutrals.white500
      : props.theme.colors.neutrals.black500};
  height: calc(100vh - 3.75rem);
    
  ${(props) =>
    props.$withBanner &&
    `
    height: calc(100vh - 3.75rem - 4.875rem);
  `}

  ${(props) => props.theme.breakpoints.up("medium")} {
    height: calc(100vh - 5.375rem);
    padding: 0 1.5rem 1.5rem 1.5rem;

    ${(props) =>
      props.$withBanner &&
      `
        height: calc(100vh - 5.375rem - 3.375rem);
    `}
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    position: relative;
    height: 100%;
    top: auto;
    left: auto;
    padding: 0;
    overflow: visible;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    background: transparent;
    gap: 1rem;
    opacity: 1;
    visibility: visible;
  }

  ${(props) =>
    props.$isOpen &&
    `
    opacity: 1;
    visibility: visible;
    transition: opacity 200ms linear, visibility 200ms 0ms linear;
  `}
`;

export const Toggle = Styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;

  ${(props) => props.theme.breakpoints.up("medium")} {
    margin-right: 1rem;
  }
`;

export interface ToggleIconProps {
  $isOpen?: boolean;
}

export const ToggleIcon = Styled.img.attrs<ToggleIconProps>((props) => ({
  // @ts-expect-error We know that map cover the cases
  src: NAV_TOGGLE_ASSETS[props.theme.mode][props.$isOpen ? "close" : "open"],
  alt: "Navigation toggle",
}))<ToggleIconProps>`
  display: block;
  width: 1.5rem;
  height: 1.5rem;
  cursor: pointer;
`;

export interface SubMenuProps {
  $isOpen?: boolean;
}

export const SubMenu = Styled.ul<SubMenuProps>`
  position: relative;
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  transition: 200ms linear;
  max-height: 0;
  margin-top: -1rem;
  overflow: hidden;
  z-index: 1;

  ${(props) =>
    props.$isOpen &&
    `
    max-height: 20rem;
  `}

  ${(props) => props.theme.breakpoints.up("large")} {
    position: absolute;
    top: calc(100% - 1rem);
    margin: 0;
    left: -1.5rem;
    gap: 0;
    padding: 0.5rem;
    overflow: visible;
    max-height: none;
    border-radius: 0.75rem;
    border: ${(props) =>
      `1px solid ${
        props.theme.mode === "light"
          ? props.theme.colors.neutrals.gray200
          : props.theme.colors.neutrals.gray800
      }`};
    background-color: ${(props) =>
      props.theme.mode === "light"
        ? props.theme.colors.neutrals.white500
        : props.theme.colors.neutrals.gray900};

    ${(props) =>
      props.theme.mode === "light" &&
      `
      box-shadow: 0px 2px 16px -4px rgba(17, 17, 17, 0.12);
    `}
  }
`;

export const SubMenuItem = Styled.li`
  &:last-child {
    margin-bottom: 0.25rem;
  }
`;

export const SubMenuItemLink = Styled.a`
  display: flex;
  text-decoration: none;
  flex-direction: column;
  justify-content: center;
  height: 2rem;
  padding: 0 0.75rem;

  ${(props) => props.theme.breakpoints.up("large")} {
    transition: background-color 200ms linear;
    height: 3.625rem;
    padding: 0 2.5rem 0 0.75rem;
    border-radius: 0.5rem;

    &:hover {
      background-color: ${(props) =>
        props.theme.mode === "light"
          ? props.theme.colors.neutrals.gray100
          : props.theme.colors.neutrals.gray800};
    }
  }
`;
export const SubMenuItemLabel = Styled.span`
  display: flex;
  gap: 0.5rem;

  ${(props) => getTextStyle(props.theme, "L2")}
  color: ${(props) => getTextColor(props.theme, "default")};
  white-space: nowrap;

  ${(props) => props.theme.breakpoints.up("medium")} {
    ${(props) => getTextStyle(props.theme, "L3")}
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    color: ${(props) => getTextColor(props.theme, "activated")};
  }
`;

export interface SubMenuItemTextProps {
  $variant: "regular" | "magic";
}

export const SubMenuItemText = Styled.span<SubMenuItemTextProps>`
  ${(props) =>
    props.$variant === "magic" &&
    `
    ${getMagicTextColor(props.theme)}
  `}
`;

export const SubMenuItemDescription = Styled.span`
  ${(props) => getTextStyle(props.theme, "B4", "medium")}
  color: ${(props) => getTextColor(props.theme, "default")};
  display: none;
  white-space: nowrap;

  ${(props) => props.theme.breakpoints.up("large")} {
    display: block;
  }
`;

export const Menu = Styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  
  ${(props) => props.theme.breakpoints.up("large")} {
    flex-direction: row;
    height: 100%;
    margin: 0;
    gap: 1.5rem;
  }
`;

export const MenuItem = Styled.li`
  ${(props) => props.theme.breakpoints.up("large")} {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    position: relative;
    border: 0;

    ${SubMenu} {
      transition: opacity 200ms linear, visibility 0ms 200ms linear, transform 200ms linear;
      transform: translateY(-0.5rem);
      opacity: 0;
      visiblity: hidden;
      pointer-events: none;
    }

    &:hover ${SubMenu} {
      transition: opacity 200ms linear, visibility 200ms 0 linear, transform 200ms linear;
      transform: translateY(0);
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
    }
  }

  &:not(:last-child):after {
    content: "";
    display: block;
    width: 100%;
    margin-top: 1rem;
    height: 1px;
    background-color: ${(props) =>
      props.theme.mode === "light"
        ? props.theme.colors.neutrals.gray200
        : props.theme.colors.neutrals.gray800};
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    &:not(:last-child):after {
      display: none;
    }
  }
`;

export interface MenuItemLinkProps {
  $withSubMenu?: boolean;
}

export const MenuItemLink = Styled.a<MenuItemLinkProps>`
  ${(props) => getTextStyle(props.theme, "L2")}
  color: ${(props) => getTextColor(props.theme, "activated")};
  
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-decoration: none;
  white-space: nowrap;

  ${(props) =>
    props.$withSubMenu &&
    `
    padding: 1.5rem 0;
  `}

  ${(props) =>
    !props.$withSubMenu &&
    `
    margin-top: 1rem;
    height: 2.375rem;
  `}

  ${(props) => props.theme.breakpoints.up("medium")} {
    ${(props) => getTextStyle(props.theme, "L3")}
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    overflow: hidden;
    height: auto;
    margin: 0;
    padding: 0;
  }
`;

export interface MenuItemLinkTextProps {
  $variant: "regular" | "magic";
}

export const MenuItemText = Styled.span<MenuItemLinkTextProps>`
  display: flex;
  gap: 0.25rem;
  align-items: center;
  justify-content: center;

  ${(props) =>
    props.$variant === "magic" &&
    `
      ${getMagicTextColor(props.theme)}
  `}
`;

export const MenuItemIcon = Styled.img.attrs(() => ({
  src: magicStarIconPurple,
  alt: "Magic icon",
}))`
  display: block;
  width: 1.5rem;
  height: 1.5rem;
`;

export const MenuItemToggle = Styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: -0.75rem;
  width: 3rem;
  height: 3rem;
  cursor: pointer;

  ${(props) => props.theme.breakpoints.up("large")} {
    display: none;
  }
`;

export interface MenuItemToggleIconProps {
  $isOpen?: boolean;
}

export const MenuItemToggleIcon = Styled.img.attrs<MenuItemToggleIconProps>(
  (props) => ({
    // @ts-expect-error We know that map cover the cases
    src: MENU_ITEM_TOGGLE_ASSETS[props.theme.mode][
      props.$isOpen ? "collapse" : "expand"
    ],
    alt: "Submenu toggle",
  }),
)<ToggleIconProps>`
  display: block;
  width: 1.5rem;
  height: 1.5rem;
  cursor: pointer;
`;

export const App = Styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  ${(props) => props.theme.breakpoints.up("large")} {
    flex-direction: row;
    align-items: center;
    gap: 1.5rem;
  }
`;

export const AppSignup = Styled.div`
  display: block;
  width: 100%;
  margin-top: 1rem;

  a:last-child, a:last-child button {
    width: 100%;
  }

  ${(props) => props.theme.breakpoints.up("medium")} {
    display: none;
    width: auto;
    margin: 0;

    a:last-child, a:last-child button {
      width: auto;
    }
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    display: block;
  }
`;

export const Controls = Styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.25rem;

  ${(props) => props.theme.breakpoints.up("large")} {
    display: none;
  }
`;

export const ControlsSignup = Styled.div`
  display: none;

  ${(props) => props.theme.breakpoints.up("medium")} {
    display: flex;
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    display: none;
  }
`;

export const NavigationTypeFormButton = Styled(PopupButton)`
  ${(props) => getTextStyle(props.theme, "L2")}
  color: ${(props) => getTextColor(props.theme, "activated")};

  ${(props) => props.theme.breakpoints.up("medium")} {
    ${(props) => getTextStyle(props.theme, "L3")}
  }

  position: relative;
  display: flex;
  align-items: center;
  padding: 0;
  border: 1px solid transparent;
  text-decoration: none;
  background: none;
  cursor: pointer;
  white-space: nowrap;
  outline: none;
  height: 2.375rem;

  ${(props) => props.theme.breakpoints.up("large")} {
    height: 100%;
  }
`;

export const GhostButton = Styled.a`
  ${(props) => getTextStyle(props.theme, "L2")}
  color: ${(props) => getTextColor(props.theme, "activated")};

  ${(props) => props.theme.breakpoints.up("medium")} {
    ${(props) => getTextStyle(props.theme, "L3")}
  }

  position: relative;
  display: flex;
  align-items: center;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  height: 2.375rem;

  ${(props) => props.theme.breakpoints.up("large")} {
    height: 100%;
  }
`;
